import type { ClosingReviewState } from './closing-model';
import { CLOSING_UPLOAD_SECONDARY_ROW_MIN } from './closing-styles';

type ReviewTone = 'incomplete' | 'pending' | 'approved' | 'changes';

interface ClosingReviewStatusCardProps {
  review: ClosingReviewState;
  hasSubmission: boolean;
  /** Footer inside the document panel (no outer card chrome). */
  variant?: 'standalone' | 'embedded';
  /** Stretch to fill the grid cell (ClosingDocumentUpload). */
  fillCell?: boolean;
}

function reviewTone(review: ClosingReviewState, hasSubmission: boolean): ReviewTone {
  if (!hasSubmission) return 'incomplete';
  if (review.status === 'APPROVED') return 'approved';
  if (review.status === 'REVIEW_FEEDBACK') return 'changes';
  return 'pending';
}

function reviewStatusLabel(tone: ReviewTone): string {
  if (tone === 'incomplete') return 'Incomplete';
  if (tone === 'approved') return 'Approved';
  if (tone === 'changes') return 'Changes requested';
  return 'Pending review';
}

function statusPillClass(tone: ReviewTone): string {
  if (tone === 'approved') return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200';
  if (tone === 'changes') return 'bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200';
  if (tone === 'pending') return 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-200';
  return 'bg-gray-200 text-gray-700 dark:bg-white/10 dark:text-gray-300';
}

function reviewDetail(
  review: ClosingReviewState,
  hasSubmission: boolean,
  feedbackText: string | null,
  changesHint = 'Upload an updated file.',
): string | null {
  if (review.status === 'APPROVED') return null;
  if (feedbackText) return feedbackText;
  if (!hasSubmission) return null;
  if (review.status === 'REVIEW_FEEDBACK') return changesHint;
  return null;
}

export function ClosingReviewStatusRow({
  label,
  tone,
  detail,
  dashed = false,
}: {
  label: string;
  tone: ReviewTone;
  detail?: string | null;
  dashed?: boolean;
}) {
  const shell = dashed
    ? 'border border-dashed border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5'
    : 'border border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-white/5';

  return (
    <div
      className={`flex h-full w-full ${CLOSING_UPLOAD_SECONDARY_ROW_MIN} items-center justify-between gap-3 rounded-xl px-3 py-2.5 ${shell}`}
      role="region"
      aria-label="Review status"
    >
      <span className={`inline-flex shrink-0 items-center rounded-full px-3.5 py-1.5 text-xs font-semibold leading-none ${statusPillClass(tone)}`}>
        {label}
      </span>
      {detail ? (
        <p className="min-w-0 text-right text-sm leading-snug text-gray-600 dark:text-gray-300">{detail}</p>
      ) : null}
    </div>
  );
}

/** Status pill drawn on top of a document preview. Feedback text expands the same overlay. */
export function ClosingReviewOverlay({
  review,
  hasSubmission,
  changesHint,
}: {
  review: ClosingReviewState;
  hasSubmission: boolean;
  /** Shown for changes requested when the review has no written feedback. */
  changesHint?: string;
}) {
  const tone = reviewTone(review, hasSubmission);
  const feedbackText = review.latestFeedbackMessage?.trim() || null;
  const detail = reviewDetail(review, hasSubmission, feedbackText, changesHint);

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      <div className="absolute inset-0 bg-gray-950/40 dark:bg-black/55" aria-hidden />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="max-w-[92%] rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-center shadow-lg dark:border-white/15 dark:bg-gray-950">
          <span className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-semibold leading-none ${statusPillClass(tone)}`}>
            {reviewStatusLabel(tone)}
          </span>
          {detail ? (
            <p className="mt-1.5 max-h-28 overflow-y-auto text-xs font-medium leading-snug text-gray-950 dark:text-white">
              {detail}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function ClosingReviewStatusCard({
  review,
  hasSubmission,
}: ClosingReviewStatusCardProps) {
  const tone = reviewTone(review, hasSubmission);
  const feedbackText = review.latestFeedbackMessage?.trim() || null;

  return (
    <ClosingReviewStatusRow
      label={reviewStatusLabel(tone)}
      tone={tone}
      detail={reviewDetail(review, hasSubmission, feedbackText)}
    />
  );
}
